import { Search, Database, Wrench } from "lucide-react";
import { ToolCard, Markdown } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for ToolCard — the collapsible tool-call surface for AIKO
 * chat, now wearing the Onyx hex-notch silhouette (`nx-s`). Verify in a real
 * browser (shape() clip): the hairline nx-rim keyline traces the notch including
 * the top-right chamfer, the .elev-card drop-shadow survives the clip (no slice
 * at the notch), and the chrome stays MONOCHROME across every status — color
 * reads only from the StatusChip on the right. Toggle a card to confirm the
 * disclosure height animation + the interior border-t divider still read.
 */
export function ToolCardDemo() {
  return (
    <Section
      id="tool-card"
      title="ToolCard"
      description="Collapsible tool-call surface for AIKO chat. The card wears the hex-notch silhouette (nx-s); elevation is a clip-safe .elev-card drop-shadow and the outline is a continuous nx-rim ring (a real border is sliced at the notch chamfer). Chrome stays monochrome (#162) — status reads only from the shared StatusChip. pending → Running shimmer (body open), success → Done dot (body collapsed), error → Failed dot (body open)."
    >
      <VariantBlock
        label="Status — running · done · failed"
        caption="monochrome notched frame; status reads from the StatusChip only"
      >
        <div className="flex flex-col gap-3 max-w-md">
          <ToolCard
            icon={<Search className="size-3.5" />}
            label="search_experiences"
            status="pending"
            args={<pre className="text-xs">{JSON.stringify({ q: "ml" }, null, 2)}</pre>}
          />
          <ToolCard
            icon={<Database className="size-3.5" />}
            label="fetch_collection"
            status="success"
            args={<pre className="text-xs">{JSON.stringify({ id: "col_42" }, null, 2)}</pre>}
            result={<Markdown content={"Found **3 experiences** in the collection."} />}
          />
          <ToolCard
            icon={<Wrench className="size-3.5" />}
            label="publish_studio"
            status="error"
            result={<Markdown content={"`PermissionError`: workspace not authorized."} />}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="No body — header-only call"
        caption="no disclosure chevron; the keyline + elevation still trace the notch"
      >
        <div className="max-w-md">
          <ToolCard label="get_current_time" status="success" />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Args + result both populated"
        caption="interior border-t divider sits inside the clip — not the silhouette outline"
      >
        <div className="max-w-md">
          <ToolCard
            icon={<Search className="size-3.5" />}
            label="search_web"
            status="success"
            defaultOpen
            args={
              <pre className="text-xs">
                {JSON.stringify({ query: "onyx hex notch surface", limit: 5 }, null, 2)}
              </pre>
            }
            result={
              <Markdown content={"Returned **5 results**. Top hit: _Onyx material spec_."} />
            }
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
